import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  MessageCircle, X, Send, ChevronLeft, Loader2,
  CornerUpLeft, Trash2, XCircle, Phone, MapPin,
  Car, Fuel, Hash,
} from 'lucide-react';

const API     = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/^http/, 'ws');

const VEHICLE_TYPE_LABELS = {
  car: 'Car', bike: 'Bike', ev: 'EV', commercial: 'Commercial',
};

const isMobileDevice = () => window.innerWidth < 768;

export default function ChatWidget() {
  const { user, token } = useAuth();
  const [open, setOpen]               = useState(false);
  const [view, setView]               = useState('list');
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv]   = useState(null);
  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState('');
  const [unread, setUnread]           = useState(0);
  const [loading, setLoading]         = useState(false);
  const [sending, setSending]         = useState(false);
  const [replyTo, setReplyTo]         = useState(null);
  const [menuMsgId, setMenuMsgId]     = useState(null);
  const [deleteConvConfirm, setDeleteConvConfirm] = useState(false);

  // ── Panel drag state (mobile only) ────────────────────────────────────────
  const PANEL_W       = () => Math.min(window.innerWidth - 16, 360);
  const PANEL_H       = 520;
  const FAB_SIZE      = 50;
  const PAD           = 8;

  const defaultPanelPos = () => ({
    x: window.innerWidth  - PANEL_W() - PAD,
    y: Math.max(PAD, window.innerHeight - PANEL_H - 80),   // above bottom nav
  });
  const defaultFabPos = () => ({
    x: window.innerWidth  - FAB_SIZE - PAD,
    y: window.innerHeight - FAB_SIZE - 120,
  });

  const [panelPos, setPanelPos] = useState(defaultPanelPos);
  const [fabPos,   setFabPos]   = useState(defaultFabPos);

  const panelDrag = useRef({ active: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const fabDrag   = useRef({ active: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const panelMoved = useRef(false);
  const fabMoved   = useRef(false);

  const wsRef     = useRef(null);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);
  const menuRef   = useRef(null);

  // Re-clamp on resize
  useEffect(() => {
    const fn = () => {
      setPanelPos(p => ({
        x: Math.max(PAD, Math.min(p.x, window.innerWidth  - PANEL_W() - PAD)),
        y: Math.max(PAD, Math.min(p.y, window.innerHeight - PANEL_H   - PAD)),
      }));
      setFabPos(p => ({
        x: Math.max(PAD, Math.min(p.x, window.innerWidth  - FAB_SIZE - PAD)),
        y: Math.max(PAD, Math.min(p.y, window.innerHeight - FAB_SIZE - PAD)),
      }));
    };
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  // ── helpers ────────────────────────────────────────────────────────────────
  const isSeller  = useCallback((conv) => conv && user && conv.seller_id === user.id, [user]);
  const otherName = useCallback((conv) => { if (!conv || !user) return ''; return isSeller(conv) ? conv.buyer_name : conv.seller_name; }, [user, isSeller]);
  const myRole    = useCallback((conv) => { if (!conv || !user) return ''; return isSeller(conv) ? 'Buyer inquiry' : 'You are buying'; }, [user, isSeller]);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuMsgId(null); };
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('touchstart', h); };
  }, []);

  // ── fetch ─────────────────────────────────────────────────────────────────
  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try { const r = await axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } }); setConversations(r.data.conversations || []); } catch {}
  }, [token]);

  const fetchUnread = useCallback(async () => {
    if (!token) return;
    try { const r = await axios.get(`${API}/conversations/unread-count`, { headers: { Authorization: `Bearer ${token}` } }); setUnread(r.data.unread || 0); } catch {}
  }, [token]);

  useEffect(() => {
    if (!user) return;
    fetchConversations(); fetchUnread();
    const h = () => { setOpen(true); setView('list'); fetchConversations(); };
    window.addEventListener('open-chat', h);
    const iv = setInterval(fetchUnread, 15000);
    return () => { clearInterval(iv); window.removeEventListener('open-chat', h); };
  }, [user, fetchConversations, fetchUnread]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => () => wsRef.current?.close(), []);
  useEffect(() => { if (view === 'chat' && open && !loading) setTimeout(() => inputRef.current?.focus(), 150); }, [view, open, loading]);

  // ── WebSocket ─────────────────────────────────────────────────────────────
  const connectWS = useCallback((conv) => {
    if (wsRef.current) wsRef.current.close();
    try {
      const ws = new WebSocket(`${WS_BASE}/ws/chat/${conv.id}?token=${token}`);
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'message') {
            setMessages(prev => {
              const rm = data.message;
              if (prev.find(m => m.id === rm.id)) return prev;
              const idx = prev.findIndex(m => m._optimistic && m.sender_id === rm.sender_id && m.content === rm.content);
              if (idx !== -1) { const n = [...prev]; n[idx] = rm; return n; }
              return [...prev, rm];
            });
            setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, last_message: data.message.content } : c));
          }
          if (data.type === 'message_deleted')     setMessages(prev => prev.filter(m => m.id !== data.msg_id));
          if (data.type === 'conversation_deleted') { goBackToList(); setConversations(prev => prev.filter(c => c.id !== data.conv_id)); }
        } catch {}
      };
      ws.onerror = () => {};
      wsRef.current = ws;
    } catch {}
  }, [token]); // eslint-disable-line

  // ── actions ───────────────────────────────────────────────────────────────
  const openConversation = async (conv) => {
    setActiveConv(conv); setView('chat'); setLoading(true);
    setMessages([]); setReplyTo(null); setMenuMsgId(null); setDeleteConvConfirm(false);
    try {
      const r = await axios.get(`${API}/conversations/${conv.id}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(r.data.messages || []);
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, my_unread: 0 } : c));
      fetchUnread();
    } catch {} finally { setLoading(false); }
    connectWS(conv);
  };

  const goBackToList = () => {
    setView('list'); setActiveConv(null); setMessages([]);
    setReplyTo(null); setMenuMsgId(null); setDeleteConvConfirm(false);
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    fetchConversations();
  };

  const sendMessage = async () => {
    if (!input.trim() || !activeConv || sending) return;
    const text = input.trim(), rs = replyTo ? { ...replyTo } : null;
    setInput(''); setReplyTo(null); setSending(true);
    const oid = `opt-${Date.now()}`;
    const om  = { id: oid, conversation_id: activeConv.id, sender_id: user.id, sender_name: user.name, content: text, read: false, created_at: new Date().toISOString(), _optimistic: true, reply_to_id: rs?.id || null, reply_to_content: rs?.content || null, reply_to_sender: rs?.sender_name || null };
    setMessages(prev => [...prev, om]);
    setConversations(prev => prev.map(c => c.id === activeConv.id ? { ...c, last_message: text } : c));
    try {
      const r = await axios.post(`${API}/conversations/${activeConv.id}/messages`, { content: text, reply_to_id: rs?.id || null }, { headers: { Authorization: `Bearer ${token}` } });
      const rm = r.data.message;
      setMessages(prev => { if (prev.find(m => m.id === rm.id)) return prev.filter(m => m.id !== oid); return prev.map(m => m.id === oid ? rm : m); });
    } catch { setMessages(prev => prev.filter(m => m.id !== oid)); setInput(text); setReplyTo(rs); }
    finally { setSending(false); inputRef.current?.focus(); }
  };

  const deleteMessage = async (msgId) => {
    setMenuMsgId(null); setMessages(prev => prev.filter(m => m.id !== msgId));
    try { await axios.delete(`${API}/conversations/${activeConv.id}/messages/${msgId}`, { headers: { Authorization: `Bearer ${token}` } }); fetchConversations(); }
    catch { const r = await axios.get(`${API}/conversations/${activeConv.id}/messages`, { headers: { Authorization: `Bearer ${token}` } }); setMessages(r.data.messages || []); }
  };

  const deleteConversation = async () => {
    setDeleteConvConfirm(false); const cid = activeConv.id; goBackToList();
    setConversations(prev => prev.filter(c => c.id !== cid));
    try { await axios.delete(`${API}/conversations/${cid}`, { headers: { Authorization: `Bearer ${token}` } }); }
    catch { fetchConversations(); }
  };

  if (!user) return null;

  // ── Info card ─────────────────────────────────────────────────────────────
  const renderInfoCard = (conv) => {
    if (!conv) return null;
    if (isSeller(conv)) {
      const { buyer_phone: phone, buyer_city: city } = conv;
      if (!phone && !city) return null;
      return (
        <div className="cwb-info-card seller-card">
          <p className="cwb-info-name">{(conv.buyer_name || '').toUpperCase()}</p>
          {phone && <div className="cwb-info-row"><Phone size={12} color="#f97316" style={{ flexShrink: 0 }} /><span>{phone}</span></div>}
          {city  && <div className="cwb-info-row"><MapPin size={12} color="#f97316" style={{ flexShrink: 0 }} /><span>{city}</span></div>}
        </div>
      );
    }
    const { vehicle_registration_no: regNo, vehicle_fuel_type: fuel, vehicle_type, vehicle_segment: seg, vehicle_title: title } = conv;
    const vType = VEHICLE_TYPE_LABELS[vehicle_type] || vehicle_type;
    if (!regNo && !fuel && !vType && !title) return null;
    const vl = [title, seg].filter(Boolean).join(' · ');
    const sl = [vType, fuel].filter(Boolean).join(' · ');
    return (
      <div className="cwb-info-card buyer-card">
        <p className="cwb-info-name">{(conv.seller_name || '').toUpperCase()}</p>
        {vl    && <div className="cwb-info-row"><Car  size={12} color="#f97316" style={{ flexShrink: 0 }} /><span>{vl}</span></div>}
        {regNo && <div className="cwb-info-row"><Hash size={12} color="#f97316" style={{ flexShrink: 0 }} /><span>{regNo}</span></div>}
        {sl    && <div className="cwb-info-row"><Fuel size={12} color="#f97316" style={{ flexShrink: 0 }} /><span>{sl}</span></div>}
      </div>
    );
  };

  // ── Drag handlers: PANEL ──────────────────────────────────────────────────
  const onPanelHeaderDown = (e) => {
    if (!isMobileDevice()) return;
    // Don't drag if clicking a button inside header
    if (e.target.closest('button')) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    panelMoved.current = false;
    panelDrag.current  = { active: true, startX: e.clientX, startY: e.clientY, origX: panelPos.x, origY: panelPos.y };
  };
  const onPanelHeaderMove = (e) => {
    if (!panelDrag.current.active) return;
    const dx = e.clientX - panelDrag.current.startX;
    const dy = e.clientY - panelDrag.current.startY;
    if (!panelMoved.current && Math.hypot(dx, dy) < 6) return;
    panelMoved.current = true;
    const pw = PANEL_W();
    setPanelPos({
      x: Math.max(PAD, Math.min(window.innerWidth  - pw       - PAD, panelDrag.current.origX + dx)),
      y: Math.max(PAD, Math.min(window.innerHeight - PANEL_H  - PAD, panelDrag.current.origY + dy)),
    });
  };
  const onPanelHeaderUp = () => { panelDrag.current.active = false; };

  // ── Drag handlers: FAB ────────────────────────────────────────────────────
  const onFabDown = (e) => {
    if (!isMobileDevice()) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    fabMoved.current = false;
    fabDrag.current  = { active: true, startX: e.clientX, startY: e.clientY, origX: fabPos.x, origY: fabPos.y };
  };
  const onFabMove = (e) => {
    if (!fabDrag.current.active) return;
    const dx = e.clientX - fabDrag.current.startX;
    const dy = e.clientY - fabDrag.current.startY;
    if (!fabMoved.current && Math.hypot(dx, dy) < 8) return;
    fabMoved.current = true;
    setFabPos({
      x: Math.max(PAD, Math.min(window.innerWidth  - FAB_SIZE - PAD, fabDrag.current.origX + dx)),
      y: Math.max(PAD, Math.min(window.innerHeight - FAB_SIZE - PAD, fabDrag.current.origY + dy)),
    });
  };
  const onFabUp = () => {
    if (!fabDrag.current.active) return;
    fabDrag.current.active = false;
    if (!fabMoved.current) {
      // tap → toggle panel
      setOpen(o => !o);
      if (!open) { setView('list'); fetchConversations(); }
    }
  };

  // ── Styles: mobile uses left/top; desktop uses CSS class ──────────────────
  const mobile = isMobileDevice();

  const fabStyle = mobile
    ? { position: 'fixed', left: fabPos.x, top: fabPos.y, width: FAB_SIZE, height: FAB_SIZE, touchAction: 'none', userSelect: 'none', zIndex: 1200 }
    : {};

  const panelStyle = mobile && open
    ? { position: 'fixed', left: panelPos.x, top: panelPos.y, width: PANEL_W(), height: PANEL_H, zIndex: 1199 }
    : {};

  return (
    <>
      {/* ── FAB ── */}
      <button
        aria-label="Messages"
        className="cwb-fab"
        style={fabStyle}
        onPointerDown={onFabDown}
        onPointerMove={onFabMove}
        onPointerUp={onFabUp}
        onClick={mobile ? undefined : () => { setOpen(o => !o); if (!open) { setView('list'); fetchConversations(); } }}
      >
        <MessageCircle size={22} color="white" />
        {unread > 0 && <span className="cwb-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {/* ── Panel ── */}
      {open && (
        <div className="cwb-panel" style={panelStyle}>

          {/* Header — drag handle on mobile */}
          <div
            className="cwb-header"
            onPointerDown={onPanelHeaderDown}
            onPointerMove={onPanelHeaderMove}
            onPointerUp={onPanelHeaderUp}
            style={{ cursor: mobile ? 'grab' : 'default', touchAction: 'none', userSelect: 'none' }}
          >
            {/* Drag indicator pill — mobile only */}
            {mobile && (
              <div style={{ position: 'absolute', top: 5, left: '50%', transform: 'translateX(-50%)', width: 36, height: 4, borderRadius: 2, background: '#d1d5db' }} />
            )}

            {view === 'chat' && (
              <button onClick={goBackToList} className="cwb-icon-btn">
                <ChevronLeft size={20} color="#374151" />
              </button>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="cwb-header-title">
                {view === 'list'
                  ? `Messages${conversations.length > 0 ? ` (${conversations.length})` : ''}`
                  : (activeConv ? otherName(activeConv) : '')}
              </p>
              {view === 'chat' && activeConv && <p className="cwb-header-sub">{activeConv.vehicle_title}</p>}
            </div>
            {view === 'chat' && !deleteConvConfirm && (
              <button onClick={() => setDeleteConvConfirm(true)} className="cwb-icon-btn" title="Delete conversation">
                <Trash2 size={15} color="#ef4444" />
              </button>
            )}
            <button onClick={() => setOpen(false)} className="cwb-icon-btn cwb-close-btn">
              <X size={16} color="#6b7280" />
            </button>
          </div>

          {/* Delete confirm */}
          {view === 'chat' && deleteConvConfirm && (
            <div className="cwb-confirm-bar">
              <span className="cwb-confirm-text">Delete this conversation?</span>
              <button onClick={deleteConversation} className="cwb-confirm-yes">Delete</button>
              <button onClick={() => setDeleteConvConfirm(false)} className="cwb-confirm-no">Cancel</button>
            </div>
          )}

          {/* Conversation list */}
          {view === 'list' && (
            <div className="cwb-scroll">
              {conversations.length === 0 ? (
                <div className="cwb-empty">
                  <div className="cwb-empty-icon"><MessageCircle size={22} color="#d1d5db" /></div>
                  <p className="cwb-empty-title">No conversations yet</p>
                  <p className="cwb-empty-sub">Open any vehicle listing and tap<br />"Chat with Seller" to start</p>
                </div>
              ) : conversations.map(conv => {
                const name = otherName(conv), role = myRole(conv), mu = conv.my_unread || 0, ss = isSeller(conv);
                return (
                  <div key={conv.id} onClick={() => openConversation(conv)} className={`cwb-conv-row${mu > 0 ? ' unread' : ''}`}>
                    {conv.vehicle_image ? <img src={conv.vehicle_image} alt="" className="cwb-thumb" /> : <div className="cwb-thumb-ph"><MessageCircle size={15} color="#d1d5db" /></div>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p className={`cwb-conv-name${mu > 0 ? ' bold' : ''}`}>{name}</p>
                          <span className={`cwb-role-badge ${ss ? 'seller' : 'buyer'}`}>{role}</span>
                        </div>
                        {mu > 0 && <span className="cwb-unread-dot">{mu > 9 ? '9+' : mu}</span>}
                      </div>
                      <p className="cwb-conv-vehicle">{conv.vehicle_title}</p>
                      {conv.last_message && <p className="cwb-conv-preview">{conv.last_message}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Chat */}
          {view === 'chat' && (
            <>
              {renderInfoCard(activeConv)}
              <div className="cwb-scroll cwb-msg-area" onClick={() => setMenuMsgId(null)}>
                {loading && <div className="cwb-loading"><Loader2 size={22} color="#f97316" style={{ animation: 'cwb-spin 1s linear infinite' }} /></div>}
                {!loading && messages.length === 0 && <div className="cwb-empty" style={{ padding: '32px 16px' }}><p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>No messages yet. Say hello! 👋</p></div>}
                {messages.map(msg => {
                  const mine = msg.sender_id === user.id, mo = menuMsgId === msg.id;
                  return (
                    <div key={msg.id} className={`cwb-msg-row ${mine ? 'mine' : 'theirs'}`}>
                      <div className="cwb-msg-wrap">
                        {!mine && <p className="cwb-msg-sender">{msg.sender_name}</p>}
                        {msg.reply_to_content && (
                          <div className={`cwb-reply-preview ${mine ? 'mine' : 'theirs'}`}>
                            <span className="cwb-reply-preview-name">{msg.reply_to_sender || 'Unknown'}</span>
                            <span className="cwb-reply-preview-text">{msg.reply_to_content}</span>
                          </div>
                        )}
                        <div className={`cwb-bubble-row ${mine ? 'mine' : 'theirs'}`}>
                          <div className={`cwb-actions ${mine ? 'mine' : 'theirs'}`}>
                            <button className="cwb-action-btn" onClick={(e) => { e.stopPropagation(); setReplyTo({ id: msg.id, content: msg.content, sender_name: msg.sender_name }); setMenuMsgId(null); inputRef.current?.focus(); }}><CornerUpLeft size={13} /></button>
                            {mine && !msg._optimistic && <button className="cwb-action-btn delete" onClick={(e) => { e.stopPropagation(); setMenuMsgId(mo ? null : msg.id); }}><Trash2 size={13} /></button>}
                          </div>
                          <div className={`cwb-bubble ${mine ? 'mine' : 'theirs'}${msg._optimistic ? ' dim' : ''}`}>
                            <span style={{ wordBreak: 'break-word' }}>{msg.content}</span>
                            <div className="cwb-time">
                              {new Date(msg.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                              {msg._optimistic && <span style={{ fontSize: 9, marginLeft: 3 }}>sending…</span>}
                            </div>
                          </div>
                        </div>
                        {mo && mine && (
                          <div ref={menuRef} className={`cwb-delete-confirm ${mine ? 'mine' : 'theirs'}`} onClick={e => e.stopPropagation()}>
                            <span style={{ fontSize: 12, color: '#374151' }}>Delete this message?</span>
                            <button className="cwb-del-yes" onClick={() => deleteMessage(msg.id)}>Delete</button>
                            <button className="cwb-del-no" onClick={() => setMenuMsgId(null)}>Cancel</button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {replyTo && (
                <div className="cwb-reply-bar">
                  <div className="cwb-reply-bar-inner">
                    <CornerUpLeft size={13} color="#f97316" style={{ flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <p className="cwb-reply-bar-name">{replyTo.sender_name}</p>
                      <p className="cwb-reply-bar-text">{replyTo.content}</p>
                    </div>
                  </div>
                  <button className="cwb-icon-btn" onClick={() => setReplyTo(null)} style={{ flexShrink: 0 }}><XCircle size={16} color="#9ca3af" /></button>
                </div>
              )}

              <div className="cwb-input-bar">
                <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder={replyTo ? 'Write a reply…' : 'Type a message…'} className="cwb-input" />
                <button onClick={sendMessage} disabled={!input.trim() || sending} className={`cwb-send${input.trim() && !sending ? ' active' : ''}`}>
                  {sending ? <Loader2 size={15} color="white" style={{ animation: 'cwb-spin 1s linear infinite' }} /> : <Send size={15} color="white" />}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes cwb-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes cwb-in   { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }

        /* FAB — desktop only (mobile uses inline style) */
        .cwb-fab {
          position:fixed; bottom:24px; right:20px; z-index:1200;
          width:52px; height:52px; border-radius:50%;
          background:#f97316; border:none; cursor:pointer;
          display:flex; align-items:center; justify-content:center;
          box-shadow:0 4px 16px rgba(249,115,22,0.45);
          -webkit-tap-highlight-color:transparent;
        }
        .cwb-badge {
          position:absolute; top:0; right:0;
          background:#ef4444; color:white; border-radius:50%;
          width:18px; height:18px; font-size:10px; font-weight:700;
          display:flex; align-items:center; justify-content:center;
          border:2px solid white; pointer-events:none;
        }

        /* Panel — desktop uses CSS, mobile uses inline style */
        .cwb-panel {
          /* desktop default */
          position:fixed; bottom:84px; right:20px; z-index:1199;
          width:370px; height:min(540px,calc(100vh - 100px));
          background:white; border-radius:16px; border:1px solid #e5e7eb;
          display:flex; flex-direction:column; overflow:hidden;
          box-shadow:0 12px 40px rgba(0,0,0,0.22);
          animation:cwb-in 0.15s ease;
        }

        /* Header */
        .cwb-header {
          padding:14px 12px 10px; border-bottom:1px solid #f3f4f6;
          display:flex; align-items:center; gap:6px; position:relative;
          background:#fff; flex-shrink:0; min-height:52px;
        }
        .cwb-header-title { margin:0; font-weight:700; font-size:14px; color:#111; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .cwb-header-sub   { margin:0; font-size:11px; color:#9ca3af; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .cwb-icon-btn {
          background:none; border:none; cursor:pointer;
          padding:6px; display:flex; align-items:center; justify-content:center;
          border-radius:50%; flex-shrink:0; min-width:32px; min-height:32px;
          -webkit-tap-highlight-color:transparent;
        }
        .cwb-close-btn { background:#f3f4f6; }

        .cwb-confirm-bar { display:flex; align-items:center; gap:8px; padding:8px 14px; background:#fef2f2; border-bottom:1px solid #fecaca; flex-shrink:0; }
        .cwb-confirm-text { flex:1; font-size:12px; color:#991b1b; font-weight:600; }
        .cwb-confirm-yes { padding:4px 12px; border-radius:8px; border:none; cursor:pointer; background:#ef4444; color:white; font-size:12px; font-weight:600; }
        .cwb-confirm-no  { padding:4px 10px; border-radius:8px; border:1px solid #d1d5db; cursor:pointer; background:white; font-size:12px; color:#374151; }

        .cwb-info-card { margin:8px 10px 0; padding:9px 12px; border-radius:10px; flex-shrink:0; }
        .cwb-info-card.seller-card { background:linear-gradient(135deg,#fdf4ff,#f5f3ff); border:1px solid #e9d5ff; }
        .cwb-info-card.buyer-card  { background:linear-gradient(135deg,#fff7ed,#fef3c7); border:1px solid #fed7aa; }
        .cwb-info-name { margin:0 0 5px; font-size:12px; font-weight:800; letter-spacing:0.05em; color:#111; }
        .cwb-info-row  { display:flex; align-items:center; gap:6px; font-size:11px; color:#374151; margin-top:3px; line-height:1.3; }

        .cwb-scroll { flex:1; overflow-y:auto; -webkit-overflow-scrolling:touch; }
        .cwb-msg-area { padding:10px 10px 4px; display:flex; flex-direction:column; gap:6px; }

        .cwb-empty { text-align:center; padding:40px 20px; }
        .cwb-empty-icon { width:48px; height:48px; border-radius:50%; background:#f3f4f6; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; }
        .cwb-empty-title { margin:0; font-size:14px; font-weight:600; color:#374151; }
        .cwb-empty-sub   { margin:8px 0 0; font-size:12px; color:#9ca3af; line-height:1.6; }

        .cwb-conv-row { padding:11px 14px; cursor:pointer; display:flex; gap:10px; align-items:center; border-bottom:1px solid #f3f4f6; background:white; min-height:66px; transition:background 0.1s; -webkit-tap-highlight-color:rgba(249,115,22,0.06); }
        .cwb-conv-row:hover  { background:#f9fafb; }
        .cwb-conv-row.unread { background:#fff7ed; }
        .cwb-conv-row.unread:hover { background:#fef3c7; }
        .cwb-thumb    { width:46px; height:38px; object-fit:cover; border-radius:8px; flex-shrink:0; }
        .cwb-thumb-ph { width:46px; height:38px; border-radius:8px; flex-shrink:0; background:#f3f4f6; display:flex; align-items:center; justify-content:center; }
        .cwb-conv-name    { margin:0; font-weight:500; font-size:13px; color:#111; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .cwb-conv-name.bold { font-weight:700; }
        .cwb-role-badge   { display:inline-block; font-size:10px; font-weight:600; border-radius:4px; padding:1px 6px; margin-top:2px; }
        .cwb-role-badge.seller { color:#7c3aed; background:#f5f3ff; }
        .cwb-role-badge.buyer  { color:#0369a1; background:#e0f2fe; }
        .cwb-unread-dot { background:#f97316; color:white; border-radius:10px; padding:2px 7px; font-size:10px; font-weight:700; flex-shrink:0; }
        .cwb-conv-vehicle { margin:2px 0 0; font-size:11px; color:#6b7280; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .cwb-conv-preview { margin:1px 0 0; font-size:11px; color:#9ca3af; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

        .cwb-loading { text-align:center; padding:32px 0; }
        .cwb-msg-row { display:flex; margin-bottom:2px; }
        .cwb-msg-row.mine   { justify-content:flex-end; }
        .cwb-msg-row.theirs { justify-content:flex-start; }
        .cwb-msg-wrap { max-width:82%; position:relative; }
        .cwb-msg-sender { margin:0 0 2px 8px; font-size:10px; color:#9ca3af; font-weight:500; }

        .cwb-reply-preview { border-left:3px solid #f97316; padding:5px 9px; margin-bottom:2px; font-size:11px; line-height:1.4; }
        .cwb-reply-preview.mine   { background:rgba(255,255,255,0.22); border-radius:12px 12px 0 0; }
        .cwb-reply-preview.theirs { background:rgba(0,0,0,0.05); border-radius:12px 12px 0 0; }
        .cwb-reply-preview-name   { display:block; font-weight:700; color:#f97316; margin-bottom:1px; }
        .cwb-reply-preview-text   { display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:inherit; opacity:0.8; }

        .cwb-bubble-row { display:flex; align-items:flex-end; gap:4px; }
        .cwb-bubble-row.mine   { flex-direction:row-reverse; }
        .cwb-bubble-row.theirs { flex-direction:row; }

        .cwb-actions { display:flex; flex-direction:column; gap:2px; opacity:0; transition:opacity 0.15s; align-self:flex-end; padding-bottom:4px; }
        .cwb-msg-wrap:hover .cwb-actions { opacity:1; }
        @media(hover:none){ .cwb-actions { opacity:1; } }

        .cwb-action-btn { width:26px; height:26px; border-radius:50%; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; background:#f3f4f6; color:#6b7280; transition:background 0.12s; -webkit-tap-highlight-color:transparent; }
        .cwb-action-btn:hover        { background:#e5e7eb; color:#374151; }
        .cwb-action-btn.delete:hover { background:#fee2e2; color:#ef4444; }

        .cwb-bubble { padding:8px 12px; font-size:13px; line-height:1.45; transition:opacity 0.2s; }
        .cwb-bubble.mine   { border-radius:16px 16px 4px 16px; background:#f97316; color:white; }
        .cwb-bubble.theirs { border-radius:16px 16px 16px 4px; background:#f3f4f6; color:#111; }
        .cwb-bubble.dim    { opacity:0.7; }
        .cwb-time { font-size:10px; margin-top:4px; opacity:0.65; text-align:right; display:flex; align-items:center; justify-content:flex-end; gap:3px; }

        .cwb-delete-confirm { display:flex; align-items:center; gap:6px; flex-wrap:wrap; margin-top:4px; padding:6px 10px; background:#fef2f2; border-radius:10px; border:1px solid #fecaca; }
        .cwb-delete-confirm.mine   { justify-content:flex-end; }
        .cwb-delete-confirm.theirs { justify-content:flex-start; }
        .cwb-del-yes { padding:3px 10px; border-radius:7px; border:none; background:#ef4444; color:white; font-size:11px; font-weight:600; cursor:pointer; }
        .cwb-del-no  { padding:3px 8px;  border-radius:7px; border:1px solid #d1d5db; background:white; font-size:11px; color:#374151; cursor:pointer; }

        .cwb-reply-bar { display:flex; align-items:center; gap:8px; padding:8px 12px; border-top:1px solid #f3f4f6; background:#fff8f3; flex-shrink:0; }
        .cwb-reply-bar-inner { display:flex; align-items:flex-start; gap:6px; flex:1; min-width:0; }
        .cwb-reply-bar-name  { margin:0; font-size:11px; font-weight:700; color:#f97316; }
        .cwb-reply-bar-text  { margin:2px 0 0; font-size:11px; color:#6b7280; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

        .cwb-input-bar { padding:10px 12px; border-top:1px solid #f3f4f6; display:flex; gap:8px; align-items:center; background:#fff; flex-shrink:0; }
        .cwb-input { flex:1; padding:9px 14px; border-radius:22px; border:1px solid #e5e7eb; font-size:16px; outline:none; background:#f9fafb; color:#111; }
        @media(min-width:768px){ .cwb-input { font-size:13px; } }

        .cwb-send { width:38px; height:38px; border-radius:50%; flex-shrink:0; background:#e5e7eb; border:none; cursor:default; display:flex; align-items:center; justify-content:center; transition:background 0.15s; -webkit-tap-highlight-color:transparent; }
        .cwb-send.active { background:#f97316; cursor:pointer; }
      `}</style>
    </>
  );
}